import type { IdentitySessionResponse } from "../../../app/src/app/generated/api/schemas/identitySessionResponse";

async function session(url: string, signal: AbortSignal): Promise<IdentitySessionResponse> {
  const response = await fetch(url, { credentials: "include", cache: "no-store", signal });
  if (!response.ok) throw new Error("Identity is unavailable.");
  return response.json() as Promise<IdentitySessionResponse>;
}

function base64url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export async function synchronizePortalIdentity(appOrigin: string, signal: AbortSignal): Promise<string | null> {
  const [central, local] = await Promise.all([
    session(`${appOrigin}/api/auth/session`, signal),
    session("/api/identity/session", signal),
  ]);
  if (central.revision === local.revision) return local.revision;
  if (central.revision === null) {
    const response = await fetch("/api/identity/logout", { method: "POST", credentials: "same-origin", signal });
    if (!response.ok && response.status !== 401) throw new Error("Unable to end the portal session.");
    return null;
  }

  const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
  const challenge = base64url(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier))));
  const authorization = await fetch(`${appOrigin}/api/auth/client-portal/authorize`, {
    method: "POST", credentials: "include", cache: "no-store", signal,
    headers: { "Content-Type": "application/json" }, body: JSON.stringify({ challenge }),
  });
  if (!authorization.ok) throw new Error("Unable to authorize the portal session.");
  const { code } = await authorization.json() as { code: string };
  const connected = await fetch("/api/identity/connect", {
    method: "POST", credentials: "same-origin", cache: "no-store", signal,
    headers: { "Content-Type": "application/json" }, body: JSON.stringify({ code, verifier }),
  });
  if (!connected.ok) throw new Error("Unable to connect the portal session.");
  return (await session("/api/identity/session", signal)).revision;
}
